import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Lock, UserPlus, Eye, EyeOff, School } from 'lucide-react';
import Button from '../common/Button';
import { authAPI, publicAPI } from '../../services/api'; 
import { useNavigate } from 'react-router-dom';

const LoginModal = ({ onClose, onLoginSuccess, defaultMode = 'login' }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLogin, setIsLogin] = useState(defaultMode === 'login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Refs for scrolling
  const modalContentRef = useRef(null);
  const formRef = useRef(null);
  const modalContainerRef = useRef(null);
  
  const [schools, setSchools] = useState([]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    school: '',
    employeeId: ''
  });

  // Fetch Schools
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await publicAPI.getSchools();
        setSchools(response.data.schools || response.data || []); 
      } catch (err) {
        console.error('Failed to load schools', err);
      }
    };
    fetchSchools();
  }, []);

  // Auto-scroll to top when switching to register - with proper offset
  useEffect(() => {
    if (!isLogin && modalContentRef.current) {
      // Scroll to top with a small delay to ensure DOM is ready
      setTimeout(() => {
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [isLogin]);

  // Reset scroll position when modal opens
  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!isLogin) {
        // Register Check
        if (formData.password !== formData.confirmPassword) throw new Error('Passwords do not match!');
        if (!formData.school) throw new Error('Please select a school');

        const registrationData = {
          fullName: formData.fullName,
          email: formData.email,
          password: formData.password,
          employeeId: formData.employeeId,
          school: formData.school,
          department: null
        };

        const response = await authAPI.registerTeacher(registrationData);
        setSuccess(response.message || 'Registration submitted successfully!');
        setTimeout(() => onClose(), 2000);
        
      } else {
        // Login Logic
        const loginData = { email: formData.email, password: formData.password };
        console.log('Attempting login with:', loginData);
        
        const response = await authAPI.login(loginData);
        
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        setSuccess('Login successful!');
        if (onLoginSuccess) onLoginSuccess(response.data);
        
        setTimeout(() => {
          onClose();
          const role = response.data.user.role;
          console.log('User role after login:', role);
          
          if (role === 'admin') {
            console.log('Navigating to /admin');
            navigate('/admin');
          } else if (role === 'teacher') {
            console.log('Navigating to /teachers');
            navigate('/teachers');
          } else if (role === 'school_admin') {
            console.log('Navigating to /school-admin');
            navigate('/school-admin');
          } else if (role === 'pending_teacher') {
            alert('Account pending approval. Please wait for admin approval.');
          } else {
            console.warn('Unknown role:', role);
            navigate('/');
          }
        }, 500);
      }
    } catch (err) {
      console.error('Login/Registration error:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegisterClick = () => {
    setIsLogin(false);
    // Scroll to top when switching to register
    if (modalContentRef.current) {
      setTimeout(() => {
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 50);
    }
  };

  const handleLoginClick = () => {
    setIsLogin(true);
    // Scroll to top when switching to login
    if (modalContentRef.current) {
      setTimeout(() => {
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 50);
    }
  };

  return (
    <div 
      ref={modalContainerRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 animate-fade-in overflow-y-auto"
      style={{ paddingTop: '130px' }} // Add top padding to account for header
      onClick={(e) => {
        // Close modal if clicking outside the modal content
        if (e.target === modalContainerRef.current) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl my-8">
        {/* Modal Header - Fixed */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <School className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">{isLogin ? 'Login' : 'Register as Teacher'}</h2>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-blue-700/50 rounded-lg transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div 
          ref={modalContentRef}
          className="overflow-y-auto max-h-[calc(90vh-180px)]"
        >
          {error && (
            <div className="mx-6 mt-6 mb-4 bg-red-50 text-red-800 p-3 rounded text-sm border border-red-200">
              {error}
            </div>
          )}
          {success && (
            <div className="mx-6 mt-6 mb-4 bg-green-50 text-green-800 p-3 rounded text-sm border border-green-200">
              {success}
            </div>
          )}

          {/* Login/Register Toggle */}
          <div className="mx-6 mt-6 mb-6">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button 
                type="button" 
                onClick={handleLoginClick}
                className={`flex-1 py-3 text-sm rounded-md font-medium transition-all duration-200 ${
                  isLogin 
                    ? 'bg-white shadow-lg text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Login
              </button>
              <button 
                type="button" 
                onClick={handleRegisterClick}
                className={`flex-1 py-3 text-sm rounded-md font-medium transition-all duration-200 ${
                  !isLogin 
                    ? 'bg-white shadow-lg text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Register
              </button>
            </div>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
            {/* Registration Fields */}
            {!isLogin && (
              <div className="space-y-4 border-b border-gray-200 pb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <div className="relative">
                    <UserPlus className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <input 
                      type="text" 
                      name="fullName" 
                      value={formData.fullName} 
                      onChange={handleChange} 
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                      required 
                      placeholder="Enter your full name" 
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                  <input 
                    type="text" 
                    name="employeeId" 
                    value={formData.employeeId} 
                    onChange={handleChange} 
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                    required 
                    placeholder="Enter your employee ID" 
                  />
                </div>

                {/* School Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                  <div className="relative">
                    <School className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <select 
                      name="school" 
                      value={formData.school} 
                      onChange={handleChange} 
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white" 
                      required
                    >
                      <option value="" className="text-gray-400">Select your school</option>
                      {schools.map(s => (
                        <option key={s._id || s.id} value={s._id || s.id} className="text-gray-700">
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                  required 
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input 
                  type={showPassword ? 'text' : 'password'} 
                  name="password" 
                  value={formData.password} 
                  onChange={handleChange} 
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                  required 
                  placeholder="Enter your password"
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)} 
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password (for register only) */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                  <input 
                    type="password" 
                    name="confirmPassword" 
                    value={formData.confirmPassword} 
                    onChange={handleChange} 
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                    required 
                    placeholder="Confirm your password"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <Button 
                type="submit" 
                variant="primary" 
                fullWidth 
                disabled={loading}
                className="py-3 text-base font-medium"
              >
                {loading ? 'Processing...' : 
                 isLogin ? 'Login to Account' : 'Create Teacher Account'}
              </Button>
            </div>

            {/* Help Text */}
            {!isLogin && (
              <p className="text-xs text-gray-500 text-center pt-2">
                Note: Your account will be pending approval from the administrator.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;